/**
 * API Service
 * 
 * Modern axios-based API client with:
 * - CSRF token management (HttpOnly-compatible)
 * - Request/Response interceptors
 * - Automatic error handling
 * - Type-safe endpoints
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { AdminApiTypes, IAdmin, ICollection, ICrawling, IGroup, IInstagramCreds, IRightsRequest, IUser, OldApiTypes } from 'common';
import { API_URL } from '../constants';
import { csrfManager } from './csrf';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: Add CSRF token to non-GET requests
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add CSRF token for mutation requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
      try {
        const token = await csrfManager.getToken();
        config.headers['X-CSRF-Token'] = token;
      } catch (error) {
        console.error('Failed to get CSRF token:', error);
        // Let the request proceed - server will reject if token is required
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle errors and CSRF token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Check if response contains new CSRF token
    const newToken = response.headers['x-csrf-token'];
    if (newToken) {
      // Token refresh handled by csrfManager on next request
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      if (window.location.pathname !== '/login') {
        console.error('[auth error]', error.config?.url);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle 403 Forbidden - CSRF token might be invalid
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Clear and fetch new CSRF token
        csrfManager.clearToken();
        const token = await csrfManager.getToken();
        originalRequest.headers['X-CSRF-Token'] = token;
        
        // Retry the original request
        return apiClient(originalRequest);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

    return Promise.reject(error);
  }
);

// API helper to extract data from response
type LegacyResponse<T> = {
  err?: unknown;
  res?: T;
  result?: T;
};

const isLegacyResponse = <T>(data: unknown): data is LegacyResponse<T> => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  return 'err' in data || 'res' in data || 'result' in data;
};

const handleResponse = <T>(response: AxiosResponse<unknown>): T => {
  const { data } = response;

  if (typeof data === 'undefined' || data === null) {
    throw new Error('An error occurred');
  }
  
  if (isLegacyResponse<T>(data)) {
    if (data.err) {
      console.error(`API Error:`, data.err);
      throw data.err;
    }

    if (typeof data.res !== 'undefined') {
      return data.res as T;
    }

    if (typeof data.result !== 'undefined') {
      return data.result as T;
    }
  }

  return data as T;
};

// API Methods
const api = {
  // Authentication
  async auth(body: OldApiTypes.Admin.Index.IBodyAuth): Promise<void> {
    const response = await apiClient.post('/api/admin/auth', body);
    return handleResponse<void>(response);
  },

  async firebaseAuth(data: { uid: string; idToken: string; has_accepted_cgu: boolean }): Promise<void> {
    const response = await apiClient.post('/api/admin/firebaseauth', data);
    return handleResponse<void>(response);
  },

  async logout(): Promise<void> {
    const response = await apiClient.post('/api/admin/logout');
    return handleResponse<void>(response);
  },

  async getAdmin(): Promise<OldApiTypes.User.IResponseMe> {
    const response = await apiClient.get('/api/admin/admin/me');
    return handleResponse<OldApiTypes.User.IResponseMe>(response);
  },

  // Users
  async getUsers(body: {
    filters: { name: string };
    options: { skip?: number; limit?: number };
  }): Promise<{ users: IUser[]; total: number }> {
    const response = await apiClient.post('/api/admin/user/find', body);
    return handleResponse<{ users: IUser[]; total: number }>(response);
  },

  async logAsUser(userId: string): Promise<string> {
    const response = await apiClient.post('/api/admin/user/logas', { userId });
    return handleResponse<string>(response);
  },

  async createUser(doc: Partial<IUser>): Promise<IUser> {
    const response = await apiClient.post('/api/admin/user/create', { doc });
    return handleResponse<IUser>(response);
  },

  async updateUser(userId: string, doc: Partial<IUser>): Promise<IUser> {
    const response = await apiClient.post('/api/admin/user/update', { userId, doc });
    return handleResponse<IUser>(response);
  },

  async removeUser(userId: string): Promise<void> {
    const response = await apiClient.post('/api/admin/user/disable', { userId });
    return handleResponse<void>(response);
  },

  // Rights Requests
  async getRightsRequests(body: {
    statuses: string[];
  }): Promise<{ rightRequests: Partial<IRightsRequest>[]; total: number }> {
    const response = await apiClient.post('/api/admin/right/find', body);
    return handleResponse<{ rightRequests: Partial<IRightsRequest>[]; total: number }>(response);
  },

  // Administrators
  async getAdmins(body: {
    filters: { name: string };
    options: { skip?: number; limit?: number };
  }): Promise<{ admins: Partial<IAdmin>[]; total: number }> {
    const response = await apiClient.post('/api/admin/administrator/find', body);
    return handleResponse<{ admins: Partial<IAdmin>[]; total: number }>(response);
  },

  async createAdmin(doc: Partial<IAdmin>): Promise<IAdmin> {
    const response = await apiClient.post('/api/admin/administrator/create', { doc });
    return handleResponse<IAdmin>(response);
  },

  async removeAdmin(adminID: string): Promise<void> {
    const response = await apiClient.post('/api/admin/administrator/remove', { adminID });
    return handleResponse<void>(response);
  },

  // Collections
  async getCollections(body: {
    filters: { name: string };
    options: { skip?: number; limit?: number };
  }): Promise<{ results: ICollection[]; total: number }> {
    const response = await apiClient.post('/api/admin/collection/find', body);
    return handleResponse<{ results: ICollection[]; total: number }>(response);
  },

  async createCollection(doc: Partial<ICollection>): Promise<ICollection> {
    const response = await apiClient.post('/api/admin/collection/create', { doc });
    return handleResponse<ICollection>(response);
  },

  async updateCollection(params: Partial<ICollection>): Promise<ICollection> {
    const response = await apiClient.post('/api/admin/collection/update', params);
    return handleResponse<ICollection>(response);
  },

  async removeCollection(collectionID: string): Promise<void> {
    const response = await apiClient.post('/api/admin/collection/remove', { collectionID });
    return handleResponse<void>(response);
  },

  // Groups
  async getGroups(body: {
    filters: { name: string };
    options: { skip?: number; limit?: number };
  }): Promise<{ groups: IGroup[]; total: number }> {
    const response = await apiClient.post('/api/admin/group/find', body);
    return handleResponse<{ groups: IGroup[]; total: number }>(response);
  },

  async createGroup(body: AdminApiTypes.BodyCreateGroup): Promise<IGroup> {
    const response = await apiClient.post('/api/admin/group', body);
    return handleResponse<IGroup>(response);
  },

  async updateGroup(id: string, body: AdminApiTypes.BodyUpdateGroup): Promise<IGroup> {
    const response = await apiClient.patch(`/api/admin/group/${id}`, body);
    return handleResponse<IGroup>(response);
  },

  async deleteGroup(id: string): Promise<void> {
    const response = await apiClient.delete(`/api/admin/group/${id}`);
    return handleResponse<void>(response);
  },

  async updateGroupHashtags(body: { groupId: string; hashtags: string[] }): Promise<{ groups: IGroup[] }> {
    const response = await apiClient.post('/api/admin/group/updatehashtags', body);
    return handleResponse<{ groups: IGroup[] }>(response);
  },

  // Crawlings
  async getCrawlings(q: string): Promise<ICrawling[]> {
    const response = await apiClient.get('/api/admin/crawlings/', { params: { q } });
    return handleResponse<ICrawling[]>(response);
  },

  async getInstagramUsers(): Promise<IInstagramCreds[]> {
    const response = await apiClient.get('/api/admin/crawlings/instagramusers');
    return handleResponse<IInstagramCreds[]>(response);
  },

  async addOrUpdateCrawling(crawling: Partial<ICrawling>): Promise<ICrawling> {
    const response = await apiClient.put('/api/admin/crawlings/', crawling);
    return handleResponse<ICrawling>(response);
  },

  async deleteCrawling(body: ICrawling): Promise<void> {
    const response = await apiClient.delete('/api/admin/crawlings/', { data: body });
    return handleResponse<void>(response);
  },

  async getInstagramUsersHashtagUsage(creds: IInstagramCreds): Promise<string[]> {
    const response = await apiClient.get('/api/admin/crawlings/instagramusersgraphusage', {
      params: { userid: creds.user_id },
    });
    return handleResponse<string[]>(response);
  },
};

export default api;

// Export axios instance for advanced usage
export { apiClient };

